import {
  EditOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { Space, Tag, Tooltip } from 'antd';
import { Link } from 'react-router-dom';

import {
  employeeProgramStatus,
  employeeProgramStatusColors,
  employeeProgramStatusPending,
} from 'apps/investment/constants/employeeProgram';
import {
  orderTypeColors,
  orderTypeLabels,
} from 'apps/investment/constants/fundOrder';
import {
  orderSideColors,
  orderSideLabels,
  programOrderMatchSourceCompanyPortfolio,
  userProgramOrderStatusColors,
} from 'apps/investment/constants/programOrder';
import { DataTableColumnHeader } from 'common/data-table-column-header';
import UIIconTooltip from 'common/ui/UIIconTooltip';
import { dateTimeCell, linkCell, numberCell } from 'utils/cell-formatter';
import { formatDateTime } from 'utils/formatDate';

export const employeeProgramsColumnsGenerator = () => {
  return [
    {
      title: '',
      dataIndex: 'id',
      align: 'center',
      render: id => (
        <Link to={`/futureflex/employee-programs/${id}`}>View</Link>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      sorter: true,
      render: value => formatDateTime(value),
    },
    {
      title: 'Employee Name',
      dataIndex: 'employee',
      render: employee => (
        <Link to={`/futureflex/employees/${employee?.id}`}>
          {employee?.full_name}
        </Link>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      render: company => (
        <Link to={`/futureflex/companies/${company?.id}`}>
          {company?.brand_name}
        </Link>
      ),
    },
    {
      title: 'Start',
      dataIndex: 'start_date',
      sorter: true,
      type: 'dateTime',
    },
    {
      title: 'End',
      dataIndex: 'end_date',
      sorter: true,
      type: 'dateTime',
    },
    {
      title: 'Company Match',
      dataIndex: 'company_contribute_amount',
      type: 'formatNumber',
      sorter: true,
    },
    {
      title: 'Employee Match',
      dataIndex: 'employee_contribute_amount',
      type: 'formatNumber',
      sorter: true,
    },
    {
      title: 'Interval',
      dataIndex: 'investment_interval',
      sorter: true,
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => {
        return (
          <Space>
            <Tag color={employeeProgramStatusColors[record.status]}>
              {employeeProgramStatus[record.status]}
            </Tag>
            {record.status === employeeProgramStatusPending &&
              record.required_active_conditions &&
              record.required_active_conditions.length > 0 && (
                <Tooltip title={'Chưa đủ điều kiện'}>
                  <InfoCircleOutlined
                    style={{ color: '#b8860b', fontSize: '20px' }}
                  />
                </Tooltip>
              )}
          </Space>
        );
      },
    },
    {
      title: 'Accept Invitation',
      dataIndex: 'joined_at',
      sorter: true,
      type: 'dateTime',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space>
          <Link to={`/futureflex/employee-programs/${record.id}/edit`}>
            <UIIconTooltip title="Edit" Icon={EditOutlined} />
          </Link>

          <Link
            to={`/futureflex/employee-programs/${record.id}/investment-plans`}
          >
            <UIIconTooltip title="Investment Plan" Icon={LineChartOutlined} />
          </Link>
          <Link to={`/futureflex/employee-programs/${record.id}/portfolio`}>
            <UIIconTooltip title="View Portfolio" Icon={PieChartOutlined} />
          </Link>
        </Space>
      ),
    },
  ];
};

export const employeeProgramOrdersColumnsGenerator = () => [
  {
    accessorKey: 'id',
    header: '',
    cell: ({ getValue }) =>
      linkCell({ getValue, path: '/futureflex/program-orders' }),
  },

  {
    accessorKey: 'order_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Order Date" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'order_side',
    header: 'Order Side',
    cell: ({ getValue }) => {
      const value = getValue();
      return (
        <Tag color={orderSideColors[value]}>
          {orderSideLabels[value] || value}
        </Tag>
      );
    },
  },

  {
    header: 'Type',
    accessorKey: 'order_type',
    cell: ({ getValue, row }) => {
      const { matched_source } = row.original;

      let orderType = getValue();
      if (
        orderType == 'buy' &&
        matched_source == programOrderMatchSourceCompanyPortfolio
      ) {
        orderType = 'redistribute';
      }

      const label = orderTypeLabels[orderType] || orderType;

      return <Tag color={orderTypeColors[orderType]}>{label}</Tag>;
    },
  },

  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ getValue, row }) => {
      const value = getValue();
      const status_label = row.original.status_label;
      return (
        <Tag color={userProgramOrderStatusColors[value]}>{status_label}</Tag>
      );
    },
  },
  {
    header: 'Amount',
    accessorKey: 'amount',
    cell: numberCell,
  },
  {
    header: 'Quantity',
    accessorKey: 'quantity',
    cell: numberCell,
  },
  {
    header: 'Matched Price',
    accessorKey: 'matched_price',
    cell: numberCell,
  },

  {
    accessorKey: 'order_id',
    header: 'Fund Order',
    cell: ({ getValue }) =>
      linkCell({ getValue, path: '/investment/fund-orders' }),
  },
  {
    header: 'Product',
    accessorKey: 'product_code',
  },

  {
    accessorKey: 'created_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: dateTimeCell,
  },
  {
    accessorKey: 'updated_at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: dateTimeCell,
  },
];
